import { memo, useCallback, useMemo } from "react";
import styled from "styled-components";
import {
  ArrowUpLeftIcon,
  ArrowUturnLeftIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/outline";
import { useCopy } from "@sablier/v2-hooks";
import { useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import Button from "~/components/molecules/Button";
import Icon from "~/components/molecules/Icon";

const WrapperPartial = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    width: 100%;
    min-height: 24px;
  }
`;

const Content = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    gap: calc(${(props) => props.theme.sizes.edge} * 1 / 2);
    & > p {
      ${(props) => props.theme.styles.textParagraphMini}
      & {
        color: ${(props) => props.theme.colors.gray100};
        font-weight: 600;
        font-size: 11pt;
      }
    }
  }
`;

const Arrow = styled.div`
  color: ${(props) => props.theme.colors.white};
  transform: rotate(180deg);
  & > * {
    --size: 16px;
    --thickness: 1.8;
  }
`;

const Actions = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    height: auto;
    margin-left: calc(${(props) => props.theme.sizes.edge} * 1 / 4);
  }
`;
const Divider = styled.div`
  width: 2px;
  height: 18px;
  background-color: ${(props) => props.theme.colors.dark400};
`;

const Action = styled(Button)`
  &,
  &[data-mini="true"] {
    height: auto;
    min-height: 0;
  }
`;

const Wrapper = styled(WrapperPartial)`
  ${(props) => props.theme.medias.maxSM} {
    ${Arrow} {
      display: none;
    }
    ${Actions} {
      > *:nth-child(3),
      > *:nth-child(4) {
        display: none;
      }
    }
  }
`;

export interface Props {
  isPresizing?: boolean;
  doFill?: (value?: string) => void;
  value?: string;
}

function Resolution({ doFill, isPresizing = false, value }: Props) {
  const { t } = useT();
  const short = useMemo(() => _.toShortAddress(value, 8, -6), [value]);
  const [isCopied, doCopy] = useCopy(value || "");

  const onFill = useCallback(() => {
    if (doFill) {
      doFill(value);
    }
  }, [doFill, value]);

  if (_.isNilOrEmptyString(value) || !_.isEthereumAddress(value)) {
    if (isPresizing) {
      return <Wrapper />;
    }
    return false;
  }

  return (
    <Wrapper>
      <Content>
        <Arrow>
          <Icon value={ArrowUturnLeftIcon} />
        </Arrow>
        <Icon purpose={"identicon"} value={value} />
        <p>
          {_.capitalize(t("words.proxy"))}: {short}
        </p>
        <Actions>
          <Divider />
          <Action
            accent={"gray400"}
            appearance={"transparent"}
            left={ArrowUpLeftIcon}
            isMini
            onClick={onFill}
            title={t("words.fill")}
          />
          <Divider />
          {isCopied && (
            <Action
              accent={"gray400"}
              appearance={"transparent"}
              left={ClipboardDocumentIcon}
              isMini
              isPreview
              title={t("words.copied")}
            />
          )}
          {!isCopied && (
            <Action
              accent={"gray400"}
              appearance={"transparent"}
              left={ClipboardDocumentIcon}
              onClick={doCopy}
              isMini
              title={t("words.copy")}
            />
          )}
        </Actions>
      </Content>
    </Wrapper>
  );
}

export default memo(Resolution);
